import React, { useCallback, useEffect, useState } from "react";
import { fetchBillingHistory } from "../../utils/api";
import "./PaymentHistoryTable.css";

const PAGE_SIZE = 10;

const formatMoney = (amountCents, currency) => {
  if (amountCents === null || amountCents === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountCents) / 100);
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const STATUS_LABELS = {
  succeeded: "Paid",
  paid: "Paid",
  failed: "Failed",
  refunded: "Refunded",
  pending: "Pending",
};

const PaymentHistoryTable = ({ locationId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBillingHistory({
        locationId,
        page,
        limit: PAGE_SIZE,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load payment history.",
      );
    } finally {
      setLoading(false);
    }
  }, [locationId, page]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="pb-section">
      <h2 className="pb-section-title">Payment History</h2>
      <div className="ai-card payment-history-card">
        {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

        {loading ? (
          <p className="ai-help">Loading payment history...</p>
        ) : items.length === 0 ? (
          <p className="ai-help">No payments yet.</p>
        ) : (
          <>
            <div className="ai-table-wrapper">
              <table className="ai-table ai-table-payment-history">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>{row.productLabel}</td>
                      <td>{formatMoney(row.amount, row.currency)}</td>
                      <td>
                        <span
                          className={`payment-status-badge payment-status-${row.status}`}
                        >
                          {STATUS_LABELS[row.status] || row.status}
                        </span>
                      </td>
                      <td>
                        {row.hostedInvoiceUrl ? (
                          <a
                            href={row.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Invoice
                          </a>
                        ) : (
                          "--"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="payment-history-pagination">
                <button
                  type="button"
                  className="ai-button ai-button-secondary ai-button-outline ai-button-sm"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span className="ai-help">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="ai-button ai-button-secondary ai-button-outline ai-button-sm"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
};

export default PaymentHistoryTable;
